import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { Log } from '../../../../../../services/logger.service';

export interface VmSubscriptionAddDialogData {
  classId: string;
  /** Subscription IDs already added — used for instant duplicate guard */
  existingSubIds: string[];
  /** Existing VM subscriptions with location, used for ID+location duplicate checks. */
  existingVmSubscriptions?: Array<{ subscriptionId: string; location: string }>;
  /**
   * Determines which Azure subscription list this dialog operates on.
   * - `'vm'` (default): adds to the class VM subscription pool via `addClassVmSubscription`.
   * - `'acc'`: adds to the class Account subscription pool via `addClassAccSubscription`;
   *   quota and location fields are omitted from results.
   */
  mode?: 'vm' | 'acc';
}

export interface VmSubscriptionAddedItem {
  subscriptionId: string;
  displayName: string;
  location: string;
  coresLimit?: number;
  coresCurrentUsage?: number;
  pendingCores?: number;
  quotaCheckedAt?: string;
  providerRegistrationStatus?: string;
}

export interface VmSubscriptionAddDialogResult {
  added: VmSubscriptionAddedItem[];
}

interface AddResult {
  subId: string;
  ok: boolean;
  message: string;
  item?: VmSubscriptionAddedItem;
}

@Component({
  selector: 'app-vm-subscription-add-dialog',
  template: `
    <div class="vsa-dialog">
      <div mat-dialog-title class="vsa-dialog-title">
        <div class="vsa-title-text">
          <mat-icon>{{ data.mode === 'acc' ? 'manage_accounts' : 'dns' }}</mat-icon>
          <span>{{ data.mode === 'acc' ? 'Add Account Subscriptions' : 'Add VM Subscriptions' }}</span>
        </div>
        <button mat-icon-button mat-dialog-close matTooltip="Close" [disabled]="isProcessing">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content class="vsa-dialog-content">
        <!-- Input phase -->
        <ng-container *ngIf="!showResults">
          <p class="vsa-desc">
            Paste one or more Azure subscription IDs below.
            Accepts any mix of <strong>newlines</strong>, <strong>commas</strong>, <strong>semicolons</strong>, or <strong>tabs</strong> as separators.
            <ng-container *ngIf="data.mode !== 'acc'">
              Display names and quota data are auto-resolved from Azure.
            </ng-container>
            <ng-container *ngIf="data.mode === 'acc'">
              Display names are auto-resolved. One subscription is required per participant.
            </ng-container>
          </p>

          <mat-form-field appearance="outline" class="vsa-full-width">
            <mat-label>Subscription IDs</mat-label>
            <textarea matInput [formControl]="idsCtrl"
              rows="6"
              style="resize: vertical;"
              [placeholder]="placeholder"
              [disabled]="isProcessing">
            </textarea>
            <mat-hint>{{ parsedCount }} subscription ID(s) detected</mat-hint>
            <mat-error *ngIf="idsCtrl.hasError('required')">At least one subscription ID is required</mat-error>
          </mat-form-field>

          <div *ngIf="isProcessing" class="vsa-progress-row">
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            <span class="vsa-progress-label">Processing {{ currentIndex }} of {{ totalToProcess }}…</span>
          </div>
        </ng-container>

        <!-- Results phase -->
        <ng-container *ngIf="showResults">
          <div class="vsa-results-summary">
            <div class="vsa-summary-chip vsa-summary-ok">
              <mat-icon>check_circle</mat-icon>
              {{ addedCount }} added
            </div>
            <div class="vsa-summary-chip vsa-summary-fail" *ngIf="failedCount > 0">
              <mat-icon>cancel</mat-icon>
              {{ failedCount }} failed
            </div>
          </div>

          <div class="vsa-results-list">
            <div *ngFor="let r of results"
              [class]="'vsa-result-row ' + (r.ok ? 'ok' : 'fail')">
              <mat-icon class="vsa-result-icon">{{ r.ok ? 'check_circle' : 'cancel' }}</mat-icon>
              <div class="vsa-result-body">
                <span class="vsa-result-name">{{ r.item?.displayName || r.subId }}</span>
                <span class="vsa-result-subid">{{ r.subId }}</span>
                <span *ngIf="!r.ok" class="vsa-result-error">{{ r.message }}</span>
              </div>
            </div>
          </div>
        </ng-container>
      </mat-dialog-content>

      <mat-dialog-actions class="vsa-dialog-actions">
        <ng-container *ngIf="!showResults">
          <button mat-button [mat-dialog-close]="null" [disabled]="isProcessing">Cancel</button>
          <button mat-flat-button color="primary"
            [disabled]="parsedCount === 0 || isProcessing"
            (click)="runAdd()">
            <mat-spinner *ngIf="isProcessing" diameter="18"></mat-spinner>
            <mat-icon *ngIf="!isProcessing">playlist_add_check</mat-icon>
            {{ isProcessing ? ('Adding ' + currentIndex + '/' + totalToProcess + '…') : ('Add ' + (parsedCount > 1 ? parsedCount + ' Subscriptions' : 'Subscription')) }}
          </button>
        </ng-container>
        <ng-container *ngIf="showResults">
          <button mat-flat-button (click)="reset()" *ngIf="hasFailures">
            <mat-icon>refresh</mat-icon> Retry Failed
          </button>
          <button mat-flat-button color="primary" [mat-dialog-close]="closeResult">
            <mat-icon>done</mat-icon> Done
          </button>
        </ng-container>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .vsa-dialog { min-width: 520px; max-width: 700px; }
    .vsa-dialog-title {
      display: flex; align-items: center; justify-content: space-between;
      padding: 16px 16px 0;
    }
    .vsa-title-text {
      display: flex; align-items: center; gap: 8px;
      font-size: 1.1rem; font-weight: 600;
    }
    .vsa-title-text mat-icon { color: #1565c0; }
    .vsa-dialog-content { padding: 12px 16px; min-height: 200px; }
    .vsa-desc { margin: 0 0 16px; font-size: 0.875rem; color: #555; line-height: 1.5; }
    .vsa-full-width { width: 100%; }
    .vsa-progress-row { margin-top: 12px; }
    .vsa-progress-label {
      font-size: 0.8rem; color: #777; margin-top: 4px; display: block;
    }
    .vsa-dialog-actions {
      padding: 8px 16px 16px;
      display: flex; justify-content: flex-end; gap: 8px;
    }
    /* Results */
    .vsa-results-summary {
      display: flex; gap: 12px; margin-bottom: 16px;
    }
    .vsa-summary-chip {
      display: flex; align-items: center; gap: 6px;
      padding: 6px 14px; border-radius: 20px;
      font-size: 0.875rem; font-weight: 600;
    }
    .vsa-summary-ok { background: #e8f5e9; color: #2e7d32; }
    .vsa-summary-fail { background: #ffebee; color: #c62828; }
    .vsa-results-list { display: flex; flex-direction: column; gap: 8px; }
    .vsa-result-row {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 10px 12px; border-radius: 8px;
    }
    .vsa-result-row.ok { background: #f1f8e9; }
    .vsa-result-row.fail { background: #fff8f8; }
    .vsa-result-icon { margin-top: 2px; }
    .vsa-result-row.ok .vsa-result-icon { color: #43a047; }
    .vsa-result-row.fail .vsa-result-icon { color: #e53935; }
    .vsa-result-body { display: flex; flex-direction: column; gap: 2px; }
    .vsa-result-name { font-weight: 500; font-size: 0.9rem; }
    .vsa-result-subid { font-size: 0.78rem; font-family: monospace; color: #777; }
    .vsa-result-error { font-size: 0.78rem; color: #c62828; }
    @media (max-width: 600px) { .vsa-dialog { min-width: unset; width: 100%; } }
  `],
})
export class VmSubscriptionAddDialogComponent implements OnDestroy {
  idsCtrl = new FormControl('', Validators.required);

  isProcessing = false;
  showResults = false;
  results: AddResult[] = [];
  currentIndex = 0;
  totalToProcess = 0;

  private readonly logger = Log.create(VmSubscriptionAddDialogComponent);

  readonly placeholder =
    'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx\nyyyyyyyy-yyyy-yyyy-yyyy-yyyyyyyyyyyy';

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: VmSubscriptionAddDialogData,
    private readonly dialogRef: MatDialogRef<VmSubscriptionAddDialogComponent>,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
  ) {}

  ngOnDestroy(): void {}

  /** Number of unique, valid-looking IDs parsed from the textarea */
  get parsedCount(): number {
    return this.parseIds().length;
  }

  get addedCount(): number {
    return this.results.filter(r => r.ok).length;
  }

  get failedCount(): number {
    return this.results.filter(r => !r.ok).length;
  }

  get hasFailures(): boolean {
    return this.results.some(r => !r.ok);
  }

  get closeResult(): VmSubscriptionAddDialogResult {
    return { added: this.results.filter(r => r.ok).map(r => r.item!) };
  }

  private parseIds(): string[] {
    const raw = this.idsCtrl.value ?? '';
    return [...new Set(
      raw.split(/[\n,;\t\s]+/)
        .map(s => s.trim())
        .filter(s => s.length >= 32),
    )];
  }

  async runAdd(): Promise<void> {
    const ids = this.parseIds();
    if (!ids.length) return;

    this.isProcessing = true;
    this.results = [];
    this.currentIndex = 0;
    this.totalToProcess = ids.length;

    for (const subId of ids) {
      this.currentIndex++;
      const isAccModeDuplicate = this.data.mode === 'acc'
        && this.data.existingSubIds.some(e => e.toLowerCase() === subId.toLowerCase());
      if (isAccModeDuplicate) {
        this.results.push({ subId, ok: false, message: 'Already added to this class' });
        continue;
      }

      try {
        const apiCall = this.data.mode === 'acc'
          ? this.classService.addClassAccSubscription(this.data.classId, subId)
          : this.classService.addClassVmSubscription(this.data.classId, subId);
        const res: any = await apiCall.toPromise();
        const sub = res?.subscription ?? res;
        const item: VmSubscriptionAddedItem = this.data.mode === 'acc'
          ? {
              subscriptionId: sub?.subscriptionId ?? subId,
              displayName: sub?.displayName ?? '',
              location: '',
            }
          : {
              subscriptionId: sub?.subscriptionId ?? subId,
              displayName: sub?.displayName ?? '',
              location: sub?.location ?? '',
              coresLimit: sub?.coresLimit,
              coresCurrentUsage: sub?.coresCurrentUsage,
              pendingCores: sub?.pendingCores,
              quotaCheckedAt: sub?.quotaCheckedAt,
              providerRegistrationStatus: sub?.providerRegistrationStatus,
            };
        this.results.push({ subId, ok: true, message: sub?.displayName ?? 'Added', item });
      } catch (err: any) {
        const msg = err?.error?.message ?? err?.message ?? 'Failed to add';
        this.results.push({ subId, ok: false, message: msg });
        this.logger.e(`Failed to add subscription ${subId}:`, err);
      }
    }

    this.isProcessing = false;
    this.showResults = true;
  }

  reset(): void {
    const failedIds = this.results.filter(r => !r.ok).map(r => r.subId);
    this.idsCtrl.setValue(failedIds.join('\n'));
    this.showResults = false;
    this.results = [];
  }
}
